/**
 * EntrustedVeil — a soft 450–650ms parchment veil bridging the
 * editorial world and the operational Stripe moment.
 *
 * Per Craig: this is not decoration. It solves the discontinuity
 * between the atmospheric editorial surfaces (/pricing) and the
 * abrupt operational handoff (Stripe-hosted Checkout). A short
 * parchment veil with the phrase
 *
 *     "The commission is being entrusted ✦"
 *
 * softly closes the editorial page before the redirect, and softly
 * dissolves into the return page (/preserved/:slug) on the way back —
 * completing the ceremonial loop: entrusted → preserved.
 *
 * Restraint locks honoured:
 *   • duration ≤ ~700ms — never a loading screen
 *   • motion moves INWARD, not outward — slight fade + 2px settle
 *   • NO spinner, NO progress bar, NO loading dots
 *   • NO sweeping transitions, NO cinematic wipe, NO zoom
 *   • consistent copy — never personalised, never edition-named
 *   • redirect runs in the background; veil only fires once the
 *     Stripe URL is in hand, so slow responses never feel like
 *     latency under the veil
 *
 * The veil's opening direction is "outward to inward" (text settles
 * down 2px to its final position) — a quiet closing of the page,
 * not a transition animation.
 */
import React, { useEffect, useState } from 'react';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const VEIL_COPY = 'The commission is being entrusted ✦';

export default function EntrustedVeil({ open, fadeMs = 300 }) {
  // Mount the overlay if either state requires it. Initial `mounted`
  // mirrors `open` so the return-page case (mounted pre-veiled) shows
  // at full opacity on the very first paint.
  const [mounted, setMounted] = useState(Boolean(open));
  const [shown, setShown] = useState(Boolean(open));

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Defer one frame so CSS transition fires from 0 → 1.
      // (No-op when already shown on first paint.)
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    // Closing: ramp opacity 1 → 0 then unmount after the transition.
    setShown(false);
    const t = setTimeout(() => setMounted(false), fadeMs + 80);
    return () => clearTimeout(t);
  }, [open, fadeMs]);

  if (!mounted) return null;
  return (
    <div
      data-testid="entrusted-veil"
      aria-live="polite"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        // Warm parchment with a soft walnut edge vignette — never
        // pure white, never a flat loading screen.
        background:
          'radial-gradient(ellipse at 50% 50%, rgba(248,238,214,0.97) 0%, rgba(232,216,184,0.97) 60%, rgba(180,148,98,0.94) 100%)',
        opacity: shown ? 1 : 0,
        transition: `opacity ${fadeMs}ms ease-out`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: shown ? 'auto' : 'none',
      }}
    >
      {/* subtle paper grain — barely visible, no shouting texture */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        backgroundImage:
          'radial-gradient(rgba(168,133,76,0.06) 1px, transparent 1px)',
        backgroundSize: '4px 4px',
        opacity: 0.55,
        pointerEvents: 'none',
      }} />
      <div
        style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.4vw, 18px)',
          lineHeight: 1.5,
          color: 'rgba(58,36,14,0.86)',
          letterSpacing: '0.01em',
          textAlign: 'center',
          padding: '0 32px',
          // Text settles 2px DOWNWARD to its final resting place
          // when the veil opens — motion moves inward, not outward.
          transform: shown ? 'translateY(0)' : 'translateY(-2px)',
          transition: `transform ${fadeMs}ms ease-out, opacity ${fadeMs}ms ease-out`,
          opacity: shown ? 1 : 0,
        }}
      >
        {VEIL_COPY}
      </div>
    </div>
  );
}
